import type { ApiResponse } from '../models/ApiResponse';
import type {
  CreateDataExportJobRequest,
  CreateTableViewDataExportJobRequest,
  DataExportJobDetailsResponse,
  DataExportOptions,
  DataExportOptionsFilter,
  DataJobExportFilter,
  DataJobOverviewResponse,
  GenerateExampleFileRequest,
} from '../models/DataExport';
import BaseService from './BaseService';

class DataExportService extends BaseService {
  public static async downloadExampleFile(request: GenerateExampleFileRequest): Promise<Blob> {
    const res = await this.postRaw('/v1/exports/example', request, { responseType: 'arraybuffer' });

    if (res.status !== 200) {
      throw res.data;
    }

    const fileContent = res.data;
    const fileType = res.headers['content-type'];

    return new Blob([fileContent], { type: fileType });
  }

  public static async downloadEvidenceReport(clientFormId: string): Promise<ApiResponse<DataExportJobDetailsResponse>> {
    return this.get(`/v1/exports/${clientFormId}/evidence-report`);
  }

  public static async downloadDistributionLogs(distributionId: string): Promise<ApiResponse<DataExportJobDetailsResponse>> {
    return this.get(`/v1/exports/${distributionId}/distribution-logs`);
  }

  public static async downloadAttachment(fileId: string): Promise<Blob> {
    const res = await this.makeRequest('GET', { responseType: 'arraybuffer' }, `/v1/exports/${fileId}/attachment`);

    if (res.status !== 200) {
      throw res.data;
    }

    const fileContent = res.data;
    const fileType = res.headers['content-type'];
    return new Blob([fileContent], { type: fileType });
  }

  public static createExport(request: CreateDataExportJobRequest): Promise<ApiResponse<DataJobOverviewResponse>> {
    return this.post(`/v1/exports/create`, request);
  }

  public static getExportOptions(request: DataExportOptionsFilter): Promise<ApiResponse<DataExportOptions>> {
    return this.post(`/v1/exports/options`, request);
  }

  public static getExportJobs(filter: DataJobExportFilter = {}): Promise<ApiResponse<DataJobOverviewResponse[]>> {
    return this.post(`/v1/exports/paged`, filter);
  }

  public static cancelExport(dataJobId: string): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/exports/${dataJobId}/cancel`, {});
  }

  public static deleteExport(dataJobId: string): Promise<ApiResponse<boolean>> {
    return this.delete(`/v1/exports/${dataJobId}`);
  }

  public static undoDeleteExport(dataJobId: string): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/exports/${dataJobId}/undo-delete`);
  }

  public static createTableViewExport(request: CreateTableViewDataExportJobRequest): Promise<ApiResponse<DataJobOverviewResponse>> {
    return this.post(`/v1/exports/table-view`, request);
  }
}

export default DataExportService;
